import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withModifiers as _withModifiers, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, unref as _unref, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "left-wrapper" }
const _hoisted_2 = { class: "title" }
const _hoisted_3 = {
  key: 0,
  class: "counter"
}
const _hoisted_4 = {
  key: 0,
  class: "right-wrapper"
}
const _hoisted_5 = {
  key: 0,
  class: "status-badges"
}

import {
  computed, inject, nextTick, provide, useTemplateRef, watch, type Ref
} from 'vue';
import RcButton from '@components/RcButton/RcButton.vue';
import RcIcon from '@components/RcIcon/RcIcon.vue';
import { useInSummary } from '@shell/components/TableOfContents/composables';
import type { RcSectionProps, SectionBackground } from './types';
import { provideSectionValidity } from './useSectionValidity';

const RC_SECTION_BG_KEY = 'rc-section-background';

const name = 'RcSection';

// Register this section in form summary/table-of-contents context (if provided)

export default /*@__PURE__*/_defineComponent({
  __name: 'RcSection',
  props: /*@__PURE__*/_mergeModels({
    type: {},
    mode: {},
    background: {},
    expandable: { type: Boolean },
    expanded: { type: Boolean },
    unmountOnHide: { type: Boolean, default: false },
    title: { default: '' }
  }, {
    "expanded": { type: Boolean, ...{ default: true } },
    "expandedModifiers": {},
  }),
  emits: ["update:expanded"],
  setup(__props: any, { expose: __expose }) {

/**
 * A section container used for grouping and organizing content, with an
 * optional header that supports title, badges, actions, and expandability.
 *
 * Example:
 *
 * <RcSection title="Section title" type="secondary" mode="with-header" :expandable="false" background="secondary">
 *   <p>Section content here</p>
 * </RcSection>
 *
 * The default slot stacks whatever is written into it 16px apart, so form
 * elements go straight in and no call site needs a wrapper div for the
 * spacing.
 *
 * <RcSection title="Section title" type="secondary" mode="with-header" background="secondary">
 *   <LabeledInput label="Name" />
 *   <LabeledInput label="Description" />
 * </RcSection>
 *
 * A section that needs several groups writes RcContentGroups into that same
 * slot. The section spaces groups 24px apart, and each group stacks its own
 * content 16px apart.
 *
 * <RcSection title="Section title" type="secondary" mode="with-header" background="secondary">
 *   <RcContentGroup>
 *     <LabeledInput label="Name" />
 *     <LabeledInput label="Description" />
 *   </RcContentGroup>
 *   <RcContentGroup>
 *     <LabeledInput label="Namespace" />
 *   </RcContentGroup>
 * </RcSection>
 *
 * <RcSection title="Section title" type="secondary" mode="with-header" expandable v-model:expanded="expanded" background="secondary">
 *   <template #counter>
 *     <RcCounterBadge :count="99" type="inactive" />
 *   </template>
 *   <template #errors>
 *     <RcIcon v-clean-tooltip="'3 validation errors'" type="error" size="large" status="error" />
 *   </template>
 *   <template #badges>
 *     <RcSectionBadges :badges="[
 *       { label: 'Status', status: 'success', tooltip: 'All systems operational' },
 *       { label: 'Status', status: 'warning', tooltip: 'Degraded performance' },
 *       { label: 'Status', status: 'error', tooltip: 'Service unavailable' },
 *     ]" />
 *   </template>
 *   <template #actions>
 *     <RcSectionActions :actions="[
 *       { label: 'Action', icon: 'chevron-left', action: () => {} },
 *       { icon: 'copy', ariaLabel: 'Copy', action: () => {} },
 *       { icon: 'trash', label: 'Delete', action: () => {} },
 *     ]" />
 *   </template>
 *   <p>Section content here</p>
 * </RcSection>
 *
 * A collapsed section keeps its content mounted but hidden, so form fields inside it keep validating.
 * Content that sizes itself from the DOM on mount measures zero while hidden, and should measure again
 * once shown (see observeReveal in @components/utils/reveal). Content that can't do this can set
 * `unmountOnHide` to be unmounted while collapsed instead.
 *
 * Form fields report whether they show an error to the section they are in (see useSectionValidity). A section with
 * such fields, including ones in nested sections, shows an error icon in its #errors slot, expanded or not.
 * Filling the slot replaces the icon, and the slot's `invalid` prop says whether it would be shown. The section's
 * table of contents entry shows the same icon. Collapsing a section validates it: its fields show their errors
 * from then on, without waiting to be touched.
 */
const props = __props;

const parentBackground = inject<Ref<SectionBackground> | null>(RC_SECTION_BG_KEY, null);

const resolvedBackground = computed<SectionBackground>(() => {
  if (props.background) {
    return props.background;
  }

  const parent = parentBackground?.value ?? null;

  return parent === 'primary' ? 'secondary' : 'primary';
});

provide(RC_SECTION_BG_KEY, resolvedBackground);

const expanded = _useModel<boolean>(__props, 'expanded');

const { hasInvalid, validate } = provideSectionValidity();

watch(expanded, (isExpanded) => {
  if (!isExpanded) {
    validate();
  }
});

// Expose summary, name, and a display label on the component public instance so
// TOC discovery can access component
const displayTitle = computed(() => props.title);

const sectionRef = useTemplateRef<HTMLElement>('rc-section-summarized-container');

// Navigating to a section from the table of contents opens it if it's collapsed, then scrolls to it
function scrollTo() {
  if (props.expandable) {
    expanded.value = true;
  }

  nextTick(() => sectionRef.value?.scrollIntoView(true));
}

const { summary } = useInSummary({
  label:      displayTitle,
  scrollTo,
  elementRef: sectionRef,
  invalid:    hasInvalid,
});

__expose({
  summary, displayTitle, name
});

const hasHeader = computed(() => {
  return props.mode === 'with-header';
});

const sectionClass = computed(() => ({
  'rc-section':     true,
  'type-primary':   props.type === 'primary',
  'type-secondary': props.type === 'secondary',
  'bg-primary':     resolvedBackground.value === 'primary',
  'bg-secondary':   resolvedBackground.value === 'secondary',
}));

const contentClass = computed(() => ({
  'section-content':    true,
  'no-header':          !hasHeader.value,
  'expandable-content': props.expandable,
}));

function toggle() {
  if (props.expandable) {
    expanded.value = !expanded.value;
  }
}

return (_ctx: any,_cache: any) => {
  const _directive_clean_tooltip = _resolveDirective("clean-tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    ref: "rc-section-summarized-container",
    class: _normalizeClass(sectionClass.value)
  }, [
    (hasHeader.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["section-header", { expandable: props.expandable, collapsed: !expanded.value }]),
          onClick: toggle
        }, [
          _createElementVNode("div", _hoisted_1, [
            (props.expandable)
              ? (_openBlock(), _createBlock(RcButton, {
                  key: 0,
                  class: "toggle-button",
                  variant: "ghost",
                  "aria-expanded": expanded.value,
                  "aria-label": expanded.value ? 'Collapse section' : 'Expand section',
                  onClick: _withModifiers(toggle, ["stop"])
                }, {
                  default: _withCtx(() => [
                    _createVNode(RcIcon, {
                      type: expanded.value ? 'chevron-down' : 'chevron-right',
                      size: "medium"
                    }, null, 8, ["type"])
                  ]),
                  _: 1
                }, 8, ["aria-expanded", "aria-label"]))
              : _createCommentVNode("", true),
            _cache[3] || (_cache[3] = _createTextVNode()),
            _createElementVNode("div", _hoisted_2, [
              _renderSlot(_ctx.$slots, "title", {}, () => [
                _createTextVNode(_toDisplayString(props.title), 1)
              ]),
              _cache[1] || (_cache[1] = _createTextVNode()),
              (_ctx.$slots.counter)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _renderSlot(_ctx.$slots, "counter")
                  ]))
                : _createCommentVNode("", true),
              _cache[2] || (_cache[2] = _createTextVNode()),
              _renderSlot(_ctx.$slots, "errors", { invalid: _unref(hasInvalid) }, () => [
                (_unref(hasInvalid))
                  ? _withDirectives((_openBlock(), _createBlock(RcIcon, {
                      key: 0,
                      type: "error",
                      size: "large",
                      status: "error",
                      "data-testid": "rc-section-invalid-fields"
                    }, null, 512)), [
                      [_directive_clean_tooltip, _ctx.t('component.rcSection.invalidFields')]
                    ])
                  : _createCommentVNode("", true)
              ])
            ])
          ]),
          _cache[5] || (_cache[5] = _createTextVNode()),
          (_ctx.$slots.badges || _ctx.$slots.actions)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (_ctx.$slots.badges)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _renderSlot(_ctx.$slots, "badges")
                    ]))
                  : _createCommentVNode("", true),
                _cache[4] || (_cache[4] = _createTextVNode()),
                (_ctx.$slots.actions)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 1,
                      class: "actions",
                      onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
                    }, [
                      _renderSlot(_ctx.$slots, "actions")
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    _cache[6] || (_cache[6] = _createTextVNode()),
    (expanded.value || !props.unmountOnHide)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(contentClass.value)
        }, [
          _renderSlot(_ctx.$slots, "default")
        ], 2)), [
          [_vShow, expanded.value]
        ])
      : _createCommentVNode("", true)
  ], 2))
}
}

})